import React, {useMemo} from "react";
import {WarningRounded} from "@mui/icons-material";
import {ACard, Skeleton} from "@atiautomacao/ati-ui-library";
import CommandDonutChart, {DonutItem} from "../../Audit/AuditCommands/components/CommandDonutChart";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {AlarmSeverityCount} from "../PerformanceAnalysis/components/DailyOverviewTotalizersFormatters";

export const ALARMS_BY_SEVERITY_CHART_HEIGHT = 330;

// Cores vêm da tabela de severidades sem "#" (ex.: "f14e4e").
export const toSeverityColor = (color: string | null | undefined): string => {
    if (!color) {
        return "#9e9e9e";
    }
    return color.startsWith("#") ? color : `#${color}`;
};

export const toDonutItems = (severities: AlarmSeverityCount[]): DonutItem[] =>
    severities.map((severity) => ({
        name: severity.name,
        value: severity.count ?? 0,
        color: toSeverityColor(severity.color),
    }));

interface Props {
    // undefined = sem dado válido (ex.: falha persistente); [] = nenhuma usina selecionada (total 0).
    severities?: AlarmSeverityCount[];
    loading?: boolean;
}

/**
 * Visão Diária: alarmes ativos de teleobjeto por severidade (SGD-3817). Usa o mesmo payload, filtro de usinas e
 * ciclo de atualização dos totalizadores; Urgente + Crítica coincide com o totalizador "Alarmes ativos".
 */
export function AlarmsBySeverityChart({severities, loading = false}: Props) {
    const items = useMemo(() => toDonutItems(severities ?? []), [severities]);

    let content: JSX.Element;
    if (loading) {
        content = <Skeleton animation="wave" height={ALARMS_BY_SEVERITY_CHART_HEIGHT} variant="rounded" width={"100%"}/>;
    } else if (severities === undefined) {
        content = <DataNotFound boxStyle={{height: ALARMS_BY_SEVERITY_CHART_HEIGHT, width: '100%'}}/>;
    } else {
        content = <CommandDonutChart items={items} height={ALARMS_BY_SEVERITY_CHART_HEIGHT} showTooltip={false}/>;
    }

    return (
        <ACard
            ariaLabel={"dashboard-overview-alarms-by-severity"}
            title="Alarmes por severidade"
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: WarningRounded,
                iconTitleStyle: {color: "#2D5DE7", fontSize: 28},
            }}
        >
            {content}
        </ACard>
    );
}
